import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUpload, FiFile, FiX, FiDollarSign } = FiIcons;

const UploadSection = ({ onSubmit }) => {
  const [file, setFile] = useState(null);
  const [projectText, setProjectText] = useState('');
  const [budget, setBudget] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [consentTerms, setConsentTerms] = useState(false);
  const [newsletter, setNewsletter] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Enhanced project details for better matching
  const [projectTitle, setProjectTitle] = useState('');
  const [genre, setGenre] = useState('');
  const [location, setLocation] = useState('');
  const [productionType, setProductionType] = useState('feature');
  const [language, setLanguage] = useState('English');
  const [runtime, setRuntime] = useState('');
  const [hasMinors, setHasMinors] = useState(false);
  const [vfxLevel, setVfxLevel] = useState('medium');
  const [localSpendPercent, setLocalSpendPercent] = useState(60);

  const currencies = [
    { code: 'USD', symbol: '$' },
  ];

  const onDrop = (acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      alert('Please upload only PDF or TXT files under 20MB');
      return;
    }
    
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
      setProjectText(''); // Clear text if file is uploaded
    }
  };

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'text/plain': ['.txt']
    },
    maxSize: 20 * 1024 * 1024, // 20MB
    multiple: false
  });

  const removeFile = () => {
    setFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!consentTerms) {
      alert('Please agree to the Terms and Privacy Policy');
      return;
    }

    if (!file && !projectText.trim()) {
      alert('Please upload a file or enter project description');
      return;
    }

    setIsSubmitting(true);

    const formData = {
      file,
      projectText: projectText.trim(),
      budget: budget ? parseFloat(budget) : null,
      currency,
      consentTerms,
      newsletter,
      timestamp: new Date().toISOString(),
      // Enhanced project details
      projectTitle: projectTitle.trim(),
      genre: genre.trim(),
      location: location.trim(),
      productionType,
      language,
      runtime: runtime ? parseInt(runtime) : null,
      hasMinors,
      vfxLevel,
      localSpendPercent
    };

    try {
      await onSubmit(formData);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
        <div className="p-6 sm:p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">
            Upload your script (PDF/TXT) or paste a description
          </h2>
          <p className="text-gray-600 text-center mb-8">
            Optional: add an approximate budget for better accuracy.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* File Upload */}
            {!file && (
              <div
                {...getRootProps()}
                className={`dropzone border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  isDragActive
                    ? 'border-primary-500 bg-primary-50'
                    : isDragReject
                    ? 'border-error-500 bg-error-50'
                    : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
                }`}
              >
                <input {...getInputProps()} />
                <SafeIcon icon={FiUpload} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-lg font-medium text-gray-900 mb-2">
                  Drop your script here, or click to browse
                </p>
                <p className="text-sm text-gray-500">
                  PDF or TXT files up to 20MB
                </p>
              </div>
            )}

            {/* Uploaded File */}
            {file && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center justify-between p-4 bg-primary-50 border border-primary-200 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiFile} className="w-5 h-5 text-primary-600" />
                  <div>
                    <p className="font-medium text-primary-900">{file.name}</p>
                    <p className="text-sm text-primary-600">
                      {(file.size / (1024 * 1024)).toFixed(1)} MB
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={removeFile}
                  className="text-primary-600 hover:text-primary-800 focus-ring rounded p-1"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </motion.div>
            )}

            {/* Text Input Alternative */}
            {!file && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Or paste your project description
                </label>
                <textarea
                  value={projectText}
                  onChange={(e) => setProjectText(e.target.value)}
                  placeholder="Describe your film project, genre, setting, budget range, etc..."
                  className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                />
              </div>
            )}

            {/* Budget Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Approximate total production budget (optional)
              </label>
              <div className="flex space-x-3">
                <div className="flex-1 relative">
                  <SafeIcon icon={FiDollarSign} className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="number"
                    value={budget}
                    onChange={(e) => setBudget(e.target.value)}
                    placeholder="1000000"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {currencies.map((curr) => (
                    <option key={curr.code} value={curr.code}>
                      {curr.code}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Enhanced Project Details */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Details (for better matching)</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Project Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Title
                  </label>
                  <input
                    type="text"
                    value={projectTitle}
                    onChange={(e) => setProjectTitle(e.target.value)}
                    placeholder="My Film Project"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Genre */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Genre(s)
                  </label>
                  <input
                    type="text"
                    value={genre}
                    onChange={(e) => setGenre(e.target.value)}
                    placeholder="drama, action, comedy"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Primary Location */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Primary Filming Location
                  </label>
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="United States, United Kingdom, etc."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Production Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Production Type
                  </label>
                  <select
                    value={productionType}
                    onChange={(e) => setProductionType(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="feature">Feature Film</option>
                    <option value="tv">TV Series/Episode</option>
                    <option value="documentary">Documentary</option>
                    <option value="short">Short Film</option>
                    <option value="commercial">Commercial</option>
                    <option value="music_video">Music Video</option>
                  </select>
                </div>

                {/* Runtime */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expected Runtime (minutes)
                  </label>
                  <input
                    type="number"
                    value={runtime}
                    onChange={(e) => setRuntime(e.target.value)}
                    placeholder="90"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Primary Language */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Primary Language
                  </label>
                  <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="English">English</option>
                    <option value="Spanish">Spanish</option>
                    <option value="French">French</option>
                    <option value="German">German</option>
                    <option value="Italian">Italian</option>
                    <option value="Portuguese">Portuguese</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                {/* VFX Level */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    VFX Intensity
                  </label>
                  <select
                    value={vfxLevel}
                    onChange={(e) => setVfxLevel(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="low">Low/None</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>

                {/* Local Spend Percentage */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Local Spend %
                  </label>
                  <input
                    type="number"
                    value={localSpendPercent}
                    onChange={(e) => setLocalSpendPercent(parseInt(e.target.value) || 60)}
                    placeholder="60"
                    min="0"
                    max="100"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Has Minors */}
                <div className="flex items-center pt-8">
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={hasMinors}
                      onChange={(e) => setHasMinors(e.target.checked)}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">
                      Cast includes child actors
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Consent Checkboxes */}
            <div className="space-y-3">
              <label className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  checked={consentTerms}
                  onChange={(e) => setConsentTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  required
                />
                <span className="text-sm text-gray-700">
                  I agree to the{' '}
                  <a href="/terms" className="text-primary-600 hover:text-primary-800 underline">
                    Terms
                  </a>{' '}
                  &{' '}
                  <a href="/privacy" className="text-primary-600 hover:text-primary-800 underline">
                    Privacy
                  </a>{' '}
                  (required)
                </span>
              </label>

              <label className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  checked={newsletter}
                  onChange={(e) => setNewsletter(e.target.checked)}
                  className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">
                  Join our newsletter for updates and tips (optional)
                </span>
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting || !consentTerms || (!file && !projectText.trim())}
              className="w-full bg-primary-600 text-white py-4 px-6 rounded-lg font-semibold text-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors focus-ring"
            >
              {isSubmitting ? 'Processing...' : 'Find my incentives'}
            </button>

            <p className="text-center text-sm text-gray-500">
              It's free • No login • BETA – feedback welcome
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UploadSection;